import { Text, Button } from '@gympass/yoga';
import { FormattedMessage, useIntl } from 'react-intl';
import { getFullNameInitials } from 'utils';

import { useTimeFormat, useRandomColors } from 'hooks';

import { Tooltip } from 'components';
import { TableColumn } from 'components/Table';
import { Patient } from 'pages/PatientList/types';

import { PatientsTableProps } from './types';

import * as S from './styles';

const PatientsTable: React.FC<PatientsTableProps> = ({
  data,
  onAttentionChange,
  loading,
  showNotFoundContent = false,
}) => {
  const intl = useIntl();
  const { getRelativeTime } = useTimeFormat();
  const { colors } = useRandomColors(data.length);

  const patientsData = data.map((patient, index) => {
    return { ...patient, avatarColor: colors[index] };
  });

  const tableColumns: TableColumn[] = [
    {
      dataKey: 'fullName',
      key: 'enduser_fullname',
      title: intl.formatMessage({ id: 'PATIENT_LIST.TABLE.NAME' }),
      render: (fullName: string, { avatarColor }) => {
        return (
          <S.NameColumn>
            <S.PatientAvatar bgColor={avatarColor}>
              <S.PatientAvatarText data-testid="patientAvatarText">
                {getFullNameInitials(fullName)}
              </S.PatientAvatarText>
            </S.PatientAvatar>
            <Text.Medium data-testid="patientFullName">{fullName}</Text.Medium>
          </S.NameColumn>
        );
      },
    },
    {
      dataKey: 'totalSessions',
      key: 'enduser_total_sessions',
      title: intl.formatMessage({ id: 'PATIENT_LIST.TABLE.TOTAL_SESSIONS' }),
      render: (totalSessions: number, row: Patient) => {
        return (
          <>
            {`${totalSessions}/${row.totalCredits} ${intl.formatMessage({
              id: 'PATIENT_LIST.TABLE.SESSIONS',
            })}`}
            {row?.notifiesRenewal && (
              <S.SessinRenewBadgeContainer data-testid="sessionRenewBadge">
                <S.StyledCheckedFull />
                <S.StyledSessionRenewalBadge>
                  {intl.formatMessage({
                    id: 'SESSION_HISTORY.RENEWAL_NOTIFICATION',
                  })}
                </S.StyledSessionRenewalBadge>
              </S.SessinRenewBadgeContainer>
            )}
          </>
        );
      },
    },
    {
      dataKey: 'lastSession',
      key: 'enduser_last_session',
      title: intl.formatMessage({ id: 'PATIENT_LIST.TABLE.LAST_SESSION' }),
      render: (lastSession: string) => {
        return getRelativeTime(lastSession);
      },
    },
    {
      dataKey: 'patientAttention',
      key: 'enduser_attention',
      title: intl.formatMessage({ id: 'PATIENT_LIST.TABLE.MORE_ATTENTION' }),
      render: (patientAttention: boolean, row: Patient) => {
        return (
          <S.CentrilizedContainer>
            <S.StyledSwitch
              data-testid="attentionSwitch"
              checked={patientAttention}
              onChange={() =>
                onAttentionChange(row.cognitoId, !patientAttention)
              }
            />
          </S.CentrilizedContainer>
        );
      },
      titleRender: (title: string) => {
        return (
          <S.MoreAttentionTH>
            <S.MoreAttentionTHTitle>{title}</S.MoreAttentionTHTitle>
            <Tooltip
              title="PATIENT_LIST.TABLE.MORE_ATTETION_TOOLTIP_TITLE"
              description="PATIENT_LIST.TABLE.MORE_ATTETION_TOOLTIP_CONTENT"
            >
              <S.StyledAlertCircle
                data-testid="tooltipOpen"
                data-tip
                data-for="more_attention_tip"
                data-event="click"
              />
            </Tooltip>
          </S.MoreAttentionTH>
        );
      },
    },
    {
      dataKey: 'cognitoId',
      key: 'view_profile',
      title: '',
      render: (cognitoID: string) => {
        return (
          <S.CentrilizedContainer>
            <Button.Link
              target="_blank"
              href={`/new-provider/enduser/${cognitoID}`}
              data-testid="navigatePatientPageButton"
            >
              <FormattedMessage id="PATIENT_LIST.TABLE.SEE_PROFILE" />
            </Button.Link>
          </S.CentrilizedContainer>
        );
      },
    },
  ];

  return (
    <S.StyledTable
      loading={loading}
      data={patientsData}
      columns={tableColumns}
      showNotFoundContent={showNotFoundContent}
    />
  );
};

export default PatientsTable;
